import { useState, useEffect, useRef, useCallback } from 'react';
import './CropOverlay.css';

function buildFilter(gamma, brightness) {
  const parts = [];
  if (gamma) parts.push(`brightness(${1 + gamma})`);
  if (brightness) {
    const contrast = 1 - Math.abs(brightness) * 0.8;
    const bright = 1 + brightness * 0.5;
    parts.push(`contrast(${contrast})`);
    parts.push(`brightness(${bright})`);
  }
  return parts.length ? parts.join(' ') : undefined;
}

function CropOverlay({ imageUrl, crop, gridType, gamma, brightness, onChange }) {
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const [dragging, setDragging] = useState(null);
  const [startPos, setStartPos] = useState(null);
  const [startCrop, setStartCrop] = useState(null);
  const [imgNatural, setImgNatural] = useState(null);

  const [cols, rows] = (gridType || '1x1').split('x').map(Number);
  const cellAspect = cols / rows;

  const getNormAspect = useCallback(() => {
    if (!imgNatural) return 1;
    return cellAspect * imgNatural.h / imgNatural.w;
  }, [cellAspect, imgNatural]);

  // Clear cached dimensions when image changes
  useEffect(() => {
    setImgNatural(null);
  }, [imageUrl]);

  const handleImageLoad = () => {
    const img = imgRef.current;
    if (img) {
      setImgNatural({ w: img.naturalWidth, h: img.naturalHeight });
    }
  };

  // Reset crop when grid type changes or image loads
  useEffect(() => {
    if (!imgNatural) return;
    const normAspect = cellAspect * imgNatural.h / imgNatural.w;

    const currentW = crop.x2 - crop.x1;
    const currentH = crop.y2 - crop.y1;
    if (currentW <= 0 || currentH <= 0) {
      // Invalid crop, reset
    } else {
      const currentAspect = currentW / currentH;
      if (Math.abs(currentAspect - normAspect) < 0.02) return;
    }

    let cropW, cropH;
    if (normAspect >= 1) {
      cropW = 1;
      cropH = 1 / normAspect;
    } else {
      cropH = 1;
      cropW = normAspect;
    }
    const x1 = (1 - cropW) / 2;
    const y1 = (1 - cropH) / 2;
    onChange({ x1, y1, x2: x1 + cropW, y2: y1 + cropH });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imgNatural, cellAspect]);

  const handleMouseDown = (e, mode) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(mode);
    setStartPos({ x: e.clientX, y: e.clientY });
    setStartCrop({ ...crop });
  };

  useEffect(() => {
    if (!dragging) return;

    const handleMouseMove = (e) => {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const dx = (e.clientX - startPos.x) / rect.width;
      const dy = (e.clientY - startPos.y) / rect.height;
      const normAspect = getNormAspect();

      if (dragging === 'move') {
        const cropW = startCrop.x2 - startCrop.x1;
        const cropH = startCrop.y2 - startCrop.y1;
        const newX1 = Math.max(0, Math.min(1 - cropW, startCrop.x1 + dx));
        const newY1 = Math.max(0, Math.min(1 - cropH, startCrop.y1 + dy));
        onChange({
          x1: newX1,
          y1: newY1,
          x2: newX1 + cropW,
          y2: newY1 + cropH,
        });
      } else if (dragging === 'resize') {
        let newW = Math.max(0.05, startCrop.x2 - startCrop.x1 + dx);
        let newH = newW / normAspect;

        if (startCrop.x1 + newW > 1) {
          newW = 1 - startCrop.x1;
          newH = newW / normAspect;
        }
        if (startCrop.y1 + newH > 1) {
          newH = 1 - startCrop.y1;
          newW = newH * normAspect;
        }

        onChange({
          x1: startCrop.x1,
          y1: startCrop.y1,
          x2: startCrop.x1 + newW,
          y2: startCrop.y1 + newH,
        });
      }
    };

    const handleMouseUp = () => setDragging(null);

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, startPos, startCrop, getNormAspect, onChange]);

  const { x1, y1, x2, y2 } = crop;

  return (
    <div ref={containerRef} className="crop-container">
      <img
        ref={imgRef}
        src={imageUrl}
        alt=""
        className="crop-image"
        style={{ filter: buildFilter(gamma, brightness) }}
        onLoad={handleImageLoad}
        draggable={false}
      />

      {/* Dimmed + blurred regions around crop */}
      <div className="crop-dim" style={{
        top: 0, left: 0, right: 0,
        height: `${y1 * 100}%`,
      }} />
      <div className="crop-dim" style={{
        top: `${y2 * 100}%`, left: 0, right: 0,
        bottom: 0,
      }} />
      <div className="crop-dim" style={{
        top: `${y1 * 100}%`,
        left: 0,
        width: `${x1 * 100}%`,
        height: `${(y2 - y1) * 100}%`,
      }} />
      <div className="crop-dim" style={{
        top: `${y1 * 100}%`,
        left: `${x2 * 100}%`,
        right: 0,
        height: `${(y2 - y1) * 100}%`,
      }} />

      {/* Crop frame — draggable to translate */}
      <div
        className={`crop-frame ${dragging === 'move' ? 'dragging' : ''}`}
        style={{
          left: `${x1 * 100}%`,
          top: `${y1 * 100}%`,
          width: `${(x2 - x1) * 100}%`,
          height: `${(y2 - y1) * 100}%`,
        }}
        onMouseDown={e => handleMouseDown(e, 'move')}
      >
        {/* Resize handle — bottom-right corner */}
        <div
          className="crop-handle"
          onMouseDown={e => handleMouseDown(e, 'resize')}
        />
      </div>
    </div>
  );
}

export default CropOverlay;
